import React, { useState, useEffect, useMemo, useCallback } from "react";
import axios from "axios";
import dayjs from "dayjs";
import {
  API_get_schedule,
  API_get_weather,
  API_get_forecasted_sales_daily,
  API_get_actual_sales_daily,
  API_get_timecard_hours_split,
} from "../../../../../constants";
import { getRightworkAPI } from "../../../requests/utilities/requests";
import { getDefaultAxiosConfig } from "../../../../../utilities/requests";
import { getBusinessWeekStartDayIndex } from "../../dateRangePresets";
import DateSelector from "../../../plan/daily_top_bar_components/DateSelector";
import PrebuiltReportShell from "../PrebuiltReportShell";
import {
  displayName,
  formatCurrency as formatCurrencyShared,
  formatHours,
  formatShiftTime,
  unwrapPayload,
} from "./_shared/laborMath";

// Federal OT threshold. CA daily/7th-day rules use 8h/12h, but week-to-date
// is the most useful "are they approaching trouble?" signal for a daily
// preview, so we stick to the weekly view here.
const OT_APPROACHING_THRESHOLD = 35;
const OT_THRESHOLD = 40;

// Snap a date back to the start of the business week.
function snapToWeekStart(date, weekStartDayIndex) {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  const diff = (d.getDay() - weekStartDayIndex + 7) % 7;
  d.setDate(d.getDate() - diff);
  return d;
}

// API_get_weather, API_get_forecasted_sales_daily, and API_get_actual_sales_daily
// constants already end with "?store_id=" — callers append the store_id directly
// and continue the query string with `&`. We can't use getRightworkAPI for these
// (it adds its own `?...` and we'd end up with a doubled-up query string).
function fetchStoreScopedRange(api, storeId, startStr, endStr) {
  const url = `${api}${storeId}&date_start=${startStr}&date_end=${endStr}`;
  return axios.get(url, getDefaultAxiosConfig());
}

// Sales figures look cleaner as whole dollars on the preview card.
function formatCurrency(n) {
  return formatCurrencyShared(n, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
}

// Pick the first record matching a given date from a list of {date, value, ...}.
function findOnDate(records, dateStr) {
  if (!Array.isArray(records)) return null;
  return records.find((r) => r.date === dateStr) || null;
}

function StatCard({ label, sublabel, value, hint }) {
  return (
    <div className="border border-slate-200 rounded-lg px-4 py-3 bg-white">
      <div className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold">
        {label}
      </div>
      {sublabel && (
        <div className="text-[10px] text-slate-400">{sublabel}</div>
      )}
      <div className="text-lg font-semibold text-slate-900 mt-1">{value}</div>
      {hint && <div className="text-[11px] text-slate-500 mt-0.5">{hint}</div>}
    </div>
  );
}

function WeatherCard({ weather }) {
  if (!weather) {
    return (
      <StatCard
        label="Weather"
        value="—"
        hint="No forecast available"
      />
    );
  }
  const high = weather.maxtemp != null ? `${Math.round(weather.maxtemp)}°` : null;
  const low = weather.mintemp != null ? `${Math.round(weather.mintemp)}°` : null;
  const tempRange = [low, high].filter(Boolean).join(" – ");
  return (
    <StatCard
      label="Weather"
      value={tempRange || "—"}
      hint={weather.text || ""}
    />
  );
}

export default function DailySchedulePreviewReport({
  report,
  onBack,
  storePicker,
  homeStore,
  storeNames,
}) {
  const weekStartDayIndex = useMemo(
    () => getBusinessWeekStartDayIndex(storeNames, homeStore),
    [storeNames, homeStore]
  );
  const [selectedDate, setSelectedDate] = useState(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  });

  const incrementDay = useCallback((current, days) => {
    const next = new Date(current);
    next.setDate(next.getDate() + days);
    next.setHours(0, 0, 0, 0);
    setSelectedDate(next);
  }, []);

  const [schedule, setSchedule] = useState([]);
  const [weekTimecards, setWeekTimecards] = useState([]);
  const [weather, setWeather] = useState(null);
  const [forecastRecord, setForecastRecord] = useState(null);
  const [sdlwRecord, setSdlwRecord] = useState(null);
  const [sdlyRecord, setSdlyRecord] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [retryNonce, setRetryNonce] = useState(0);

  const dateStr = useMemo(
    () => dayjs(selectedDate).format("YYYY-MM-DD"),
    [selectedDate]
  );
  const sdlwStr = useMemo(
    () => dayjs(selectedDate).subtract(7, "day").format("YYYY-MM-DD"),
    [selectedDate]
  );
  // 52 weeks back preserves day-of-week, matching the SDLY convention.
  const sdlyStr = useMemo(
    () => dayjs(selectedDate).subtract(364, "day").format("YYYY-MM-DD"),
    [selectedDate]
  );
  // Business week containing selectedDate, used for the Approaching-OT card.
  const weekStartStr = useMemo(
    () =>
      dayjs(snapToWeekStart(selectedDate, weekStartDayIndex)).format(
        "YYYY-MM-DD"
      ),
    [selectedDate, weekStartDayIndex]
  );

  useEffect(() => {
    if (!homeStore) return undefined;

    let cancelled = false;
    setIsLoading(true);
    setError(null);

    (async () => {
      // Use allSettled so a single failing endpoint (e.g. no weather record
      // for the date) doesn't blank out the entire report. The schedule
      // fetch is the only one that's truly load-bearing — if it fails we
      // surface the error; the rest just show as "—".
      const results = await Promise.allSettled([
        getRightworkAPI(API_get_schedule, {
          store_id: homeStore,
          lower_bound_date: dateStr,
          upper_bound_date: dateStr,
          should_return_all_stores: false,
        }),
        fetchStoreScopedRange(API_get_weather, homeStore, dateStr, dateStr),
        fetchStoreScopedRange(
          API_get_forecasted_sales_daily,
          homeStore,
          dateStr,
          dateStr
        ),
        fetchStoreScopedRange(
          API_get_actual_sales_daily,
          homeStore,
          sdlwStr,
          sdlwStr
        ),
        fetchStoreScopedRange(
          API_get_actual_sales_daily,
          homeStore,
          sdlyStr,
          sdlyStr
        ),
        // Week-to-date punches for the Approaching-OT card. Range is week
        // start through selectedDate inclusive — gives us each employee's
        // running total for the business week.
        getRightworkAPI(API_get_timecard_hours_split, {
          store_id: homeStore,
          date_start: weekStartStr,
          date_end: dateStr,
        }),
      ]);

      if (cancelled) return;

      const [
        scheduleResult,
        weatherResult,
        forecastResult,
        sdlwResult,
        sdlyResult,
        weekHoursResult,
      ] = results;

      if (scheduleResult.status === "rejected") {
        setError("Couldn't load schedule. Try again or pick a different day.");
        setIsLoading(false);
        return;
      }

      // Schedule (redux-wrapped)
      const schedulePayload = unwrapPayload(scheduleResult.value);
      const scheduleList = Array.isArray(schedulePayload?.schedule)
        ? schedulePayload.schedule
        : Array.isArray(schedulePayload)
        ? schedulePayload
        : [];
      setSchedule(scheduleList);

      // Weather (plain array). Take the first record for the date. If the
      // call failed we just show "No forecast available" in the card.
      const weatherList =
        weatherResult.status === "fulfilled"
          ? unwrapPayload(weatherResult.value) || []
          : [];
      setWeather(
        Array.isArray(weatherList) ? findOnDate(weatherList, dateStr) : null
      );

      // Forecast / SDLW / SDLY — empty when the call fails; cards show "—".
      setForecastRecord(
        forecastResult.status === "fulfilled"
          ? findOnDate(unwrapPayload(forecastResult.value), dateStr)
          : null
      );
      setSdlwRecord(
        sdlwResult.status === "fulfilled"
          ? findOnDate(unwrapPayload(sdlwResult.value), sdlwStr)
          : null
      );
      setSdlyRecord(
        sdlyResult.status === "fulfilled"
          ? findOnDate(unwrapPayload(sdlyResult.value), sdlyStr)
          : null
      );

      // Week-to-date timecards — non-fatal if missing; the OT card just
      // shows "No data yet" in that case.
      setWeekTimecards(
        weekHoursResult.status === "fulfilled" &&
          Array.isArray(weekHoursResult.value?.data?.timecards)
          ? weekHoursResult.value.data.timecards
          : []
      );

      setIsLoading(false);
    })();

    return () => {
      cancelled = true;
    };
  }, [homeStore, dateStr, sdlwStr, sdlyStr, weekStartStr, retryNonce]);

  const handleRetry = useCallback(() => {
    setRetryNonce((n) => n + 1);
  }, []);

  // Sum each employee's week-to-date paid hours and surface anyone at or
  // above the approaching threshold so a manager can pre-emptively re-shuffle
  // the day before payroll lands. Sorted by projected hours descending.
  const approachingOT = useMemo(() => {
    const byStm = new Map();
    for (const tc of weekTimecards) {
      const stm = tc.store_team_member;
      if (!stm?.id) continue;
      const hours = Number(tc.total_paid_hours ?? 0);
      if (Number.isNaN(hours)) continue;
      const existing = byStm.get(stm.id) || {
        id: stm.id,
        name: displayName(stm),
        hours: 0,
      };
      existing.hours += hours;
      byStm.set(stm.id, existing);
    }
    return Array.from(byStm.values())
      .filter((r) => r.hours >= OT_APPROACHING_THRESHOLD)
      .sort((a, b) => b.hours - a.hours);
  }, [weekTimecards]);

  // Group schedule by role, sort each group by shift_start, then sort groups alphabetically.
  const groups = useMemo(() => {
    const byRole = new Map();
    for (const s of schedule) {
      const role = s.role_id?.role_name || "Unassigned";
      if (!byRole.has(role)) byRole.set(role, []);
      byRole.get(role).push(s);
    }
    return Array.from(byRole.entries())
      .map(([role, shifts]) => ({
        role,
        shifts: shifts.slice().sort((a, b) => a.shift_start - b.shift_start),
      }))
      .sort((a, b) => a.role.localeCompare(b.role));
  }, [schedule]);

  const totalShifts = schedule.length;
  const totalHours = useMemo(
    () =>
      schedule.reduce((sum, s) => {
        const h = Number(s.hours ?? s.shift_length ?? 0);
        return Number.isNaN(h) ? sum : sum + h;
      }, 0),
    [schedule]
  );

  const filters = (
    <div className="flex items-center">
      <DateSelector date={selectedDate} increment_day={incrementDay} />
    </div>
  );

  const fullDateLabel = dayjs(selectedDate).format("dddd, MMMM D, YYYY");

  return (
    <PrebuiltReportShell
      report={report}
      onBack={onBack}
      filters={filters}
      storePicker={storePicker}
      isLoading={isLoading}
    >
      {error ? (
        <div className="py-6 text-center">
          <div className="text-sm text-red-600 mb-3">{error}</div>
          <button
            type="button"
            onClick={handleRetry}
            className="text-xs px-3 py-1.5 rounded border border-violet-600 text-violet-700 hover:bg-violet-50"
          >
            Retry
          </button>
        </div>
      ) : (
        <>
          <div className="mb-4">
            <h4 className="text-sm font-semibold text-slate-700">
              {fullDateLabel}
            </h4>
            <p className="text-xs text-slate-500">
              {totalShifts} shift{totalShifts === 1 ? "" : "s"} ·{" "}
              {totalHours.toFixed(2)} scheduled hours
            </p>
          </div>

          {/* Stat row: forecast, SDLW, SDLY, weather */}
          <div className="grid grid-cols-4 lg:grid-cols-2 sm:grid-cols-1 gap-3 mb-6">
            <StatCard
              label="Forecast"
              sublabel={fullDateLabel}
              value={formatCurrency(forecastRecord?.value)}
            />
            <StatCard
              label="SDLW"
              sublabel={dayjs(selectedDate)
                .subtract(7, "day")
                .format("ddd, MMM D, YYYY")}
              value={formatCurrency(sdlwRecord?.value)}
              hint="Same day, last week"
            />
            <StatCard
              label="SDLY"
              sublabel={dayjs(selectedDate)
                .subtract(364, "day")
                .format("ddd, MMM D, YYYY")}
              value={formatCurrency(sdlyRecord?.value)}
              hint="Same day-of-week, last year"
            />
            <WeatherCard weather={weather} />
          </div>

          {/* Approaching-OT card: employees at or above the federal weekly
              threshold this business week. Hidden when nobody qualifies —
              the "all clear" state is the absence of the card. */}
          {approachingOT.length > 0 && (
            <div className="border border-slate-200 rounded-lg overflow-hidden mb-6">
              <div className="bg-amber-50 border-b border-amber-200 px-4 py-2">
                <h5 className="text-sm font-semibold text-amber-900">
                  Approaching Overtime
                </h5>
                <p className="text-[10px] text-amber-800 mt-0.5">
                  Week-to-date paid hours for the business week containing this
                  day. Highlighted in red once an employee crosses {OT_THRESHOLD} hours;
                  amber from {OT_APPROACHING_THRESHOLD} hours.
                </p>
              </div>
              <table className="w-full text-xs">
                <thead className="bg-white border-b border-slate-100 text-slate-500">
                  <tr>
                    <th className="text-left font-medium px-4 py-1.5">
                      Employee
                    </th>
                    <th className="text-right font-medium px-4 py-1.5 w-32">
                      Hours so far
                    </th>
                    <th className="text-right font-medium px-4 py-1.5 w-32">
                      Until {OT_THRESHOLD} hr
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {approachingOT.map((r) => {
                    const isOver = r.hours >= OT_THRESHOLD;
                    const remaining = Math.max(0, OT_THRESHOLD - r.hours);
                    return (
                      <tr
                        key={r.id}
                        className={`border-b border-slate-50 last:border-b-0 ${
                          isOver ? "bg-rose-50" : "bg-amber-50/50"
                        }`}
                      >
                        <td className="px-4 py-2 font-medium text-slate-800">
                          {r.name}
                        </td>
                        <td
                          className={`px-4 py-2 text-right font-semibold ${
                            isOver ? "text-rose-700" : "text-amber-800"
                          }`}
                        >
                          {formatHours(r.hours)}
                        </td>
                        <td className="px-4 py-2 text-right text-slate-700">
                          {isOver ? "—" : formatHours(remaining)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* Roster grouped by role */}
          {groups.length === 0 ? (
            <div className="text-sm text-gray-500 py-8 text-center border border-slate-200 rounded-lg">
              No published shifts on this day.
            </div>
          ) : (
            <div className="space-y-4">
              {groups.map(({ role, shifts }) => (
                <div
                  key={role}
                  className="border border-slate-200 rounded-lg overflow-hidden"
                >
                  <div className="bg-slate-50 border-b border-slate-200 px-4 py-2 flex items-baseline justify-between">
                    <h5 className="text-sm font-semibold text-slate-800">
                      {role}
                    </h5>
                    <span className="text-[11px] text-slate-500">
                      {shifts.length} shift{shifts.length === 1 ? "" : "s"}
                    </span>
                  </div>
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="border-b border-slate-100 text-slate-500">
                        <th className="text-left font-medium px-4 py-1.5 w-1/2">
                          Employee
                        </th>
                        <th className="text-left font-medium px-4 py-1.5">
                          Shift
                        </th>
                        <th className="text-left font-medium px-4 py-1.5">
                          Station
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {shifts.map((s) => (
                        <tr
                          key={s.id}
                          className="border-b border-slate-50 last:border-b-0 hover:bg-slate-50"
                        >
                          <td className="px-4 py-2 font-medium text-slate-800">
                            {displayName(s.store_team_member)}
                          </td>
                          <td className="px-4 py-2 text-slate-700">
                            {formatShiftTime(s.shift_start)} –{" "}
                            {formatShiftTime(s.shift_end)}
                          </td>
                          <td className="px-4 py-2 text-slate-500">
                            {s.station?.name || (
                              <span className="text-slate-300">—</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </PrebuiltReportShell>
  );
}
